import { useMemo } from "react";
import { USER_ROLES } from "../lib/utils/constant";

/**
 * Hook for permission checks. Works with a single permission or multiple permissions.
 *
 * When called with no arguments, reads permissions from Redux (state.user.permissions).
 * When called with an array, uses that array (useful for tests or custom sources).
 *
 * @param {string[]} [permissionsOverride] - Optional. If provided, use this array instead of Redux.
 * @returns {{ can, canAny, canAll, canDo }} Permission check API
 *
 * @example
 * const { can, canAny, canAll, canDo } = usePermissions();
 * if (can(PERMISSIONS.USER_CREATE)) { ... }
 * if (canDo('user', 'create')) { ... }
 * if (canAny([PERMISSIONS.USER_UPDATE, PERMISSIONS.USER_CREATE])) { ... }
 */
export function usePermissions(permissionsOverride) {
  const permissionFromLocalStorage = JSON.parse(localStorage.getItem('permissions'));
  const permissions = Array.isArray(permissionsOverride)
    ? permissionsOverride
    : permissionFromLocalStorage;

  const permissionSet = useMemo(() => new Set(permissions), [permissions]);

  /** Check a single permission string (e.g. "user:create") */
  const can = (permission, roleCode) => {
    if (permission == null) return false;
    return permissionSet.has(permission) || roleCode === USER_ROLES.SYS_ADMIN; 
  };

  /** Check if user has ANY of the given permissions */
  const canAny = (permissionList, roleCode) => {
    if (!Array.isArray(permissionList) || permissionList.length === 0)
      return false;
    return permissionList.some((p) => permissionSet.has(p)) || roleCode === USER_ROLES.SYS_ADMIN ;
  };

  /** Check if user has ALL of the given permissions */
  const canAll = (permissionList, roleCode) => {
    if (!Array.isArray(permissionList) || permissionList.length === 0)
      return false;
    return permissionList.every((p) => permissionSet.has(p)) || roleCode === USER_ROLES.SYS_ADMIN ;
  };

  /**
   * Check permission by resource and action (dynamic).
   * Builds "resource:action" and checks (e.g. canDo("user", "create") => "user:create").
   */
  const canDo = (resource, action) => {
    if (resource == null || action == null) return false;
    const permission = `${String(resource).trim()}:${String(action).trim()}`;
    return permissionSet.has(permission);
  };

  return {
    can,
    canAny,
    canAll,
    canDo,
  };
}
